/**
 * One realtime channel on the notifications table already exists (Header) - every row the server writes for this
 * user arrives there. This hands those rows to whoever is on screen, so a page can react without a channel of its
 * own: the group page refetches its join requests when a `join_request` lands, and the Find/Groups cache is dropped
 * when you are approved into a group (2026-10-03: the approved person saw "not in a group" until the cache aged).
 */

const EVENT_NAME = 'prii:notification';

/**
 * Notification types that change which groups you are in, or who is asking to join one. Checked against every
 * notifications insert in supabase/baseline/live_schema.sql (2026-10-16 audit): `queue_matched` was missing, so
 * someone auto-matched into a group by match_user_to_group stayed solo on Find for up to 10 min (useFindGroups
 * staleTime). `member_left` is allowed by notifications_type_check but nothing writes it today - listed so a
 * future writer works without a client change.
 */
export const MEMBERSHIP_NOTIFICATION_TYPES = new Set([
  'join_request',
  'join_approved',
  'join_denied',
  'member_joined',
  'member_left',
  'promoted_to_manager',
  'queue_matched',
]);

/**
 * Types that change who Find watches without changing your groups: a `follow` can make a follow mutual, and mutual
 * follows going to the event are on the lynx_event_peers roster (2026-10-16 audit: following back mid-event took
 * the 45 s timer to show up). `unfollow` is the silent row migration 171's trigger writes when a mutual follow ends:
 * born read and never listed, it only tells Find to stop watching (and showing you to) the unfollower now.
 */
export const FIND_PEER_NOTIFICATION_TYPES = new Set(['follow', 'unfollow']);

export function emitNotificationRow(row) {
  if (typeof window === 'undefined' || !row) return;
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: row }));
}

/** Subscribe to notification rows as they arrive. Returns the unsubscribe function. */
export function onNotificationRow(handler) {
  if (typeof window === 'undefined') return () => {};
  const listener = (e) => handler(e.detail);
  window.addEventListener(EVENT_NAME, listener);
  return () => window.removeEventListener(EVENT_NAME, listener);
}
